/**
 * vendorProfileMedia.js
 * The pure half of deleting a vendor's profile images (logo, shop banner,
 * card image) so several can go in one confirmation. No React, no axios: the
 * API call is injected, which is what makes the fan-out testable.
 *
 * Fields come from VENDOR_PROFILE_MEDIA_RULES so the dialog and the upload
 * validator can never disagree about which property holds which image.
 */
import { VENDOR_PROFILE_MEDIA_RULES } from "./imageUpload";

const rulesFor = (vendorType) =>
  VENDOR_PROFILE_MEDIA_RULES[String(vendorType || "").toLowerCase()] || {};

/** The URL a role currently resolves to on this vendor, or "". */
export const vendorProfileMediaUrl = (vendorType, media, role) => {
  const rule = rulesFor(vendorType)[role];
  if (!rule || !media) return "";
  return media[rule.field] || (rule.legacyField ? media[rule.legacyField] : "") || "";
};

/**
 * Every role that has a saved image on this vendor, in form order, shaped for
 * MediaDeleteDialog: [{ key, url, label, hint, aspect }].
 */
export const listVendorProfileMedia = (vendorType, media) =>
  Object.entries(rulesFor(vendorType))
    .map(([role, rule]) => {
      const url = vendorProfileMediaUrl(vendorType, media, role);
      if (!url) return null;
      return {
        key: role,
        url,
        label: rule.shortLabel || rule.label,
        hint: `${rule.width} x ${rule.height}`,
        aspect: `${rule.width} / ${rule.height}`,
      };
    })
    .filter(Boolean);

/** { imageUrl: "", shopBannerUrl: "", bannerUrl: "" } for the roles removed. */
export const clearedVendorProfileFields = (vendorType, roles) => {
  const rules = rulesFor(vendorType);
  const cleared = {};
  (roles || []).forEach((role) => {
    const rule = rules[role];
    if (!rule) return;
    cleared[rule.field] = "";
    if (rule.legacyField) cleared[rule.legacyField] = "";
  });
  return cleared;
};

/**
 * Remove several roles at once. Each role is its own request against the
 * existing audited endpoint, run together, and every outcome is reported:
 * a Cloudinary hiccup on the banner must not hide that the logo did go.
 *
 * @param {Function} remove  (vendorType, vendorId, role) => Promise
 * @returns {{ removed: string[], failed: [{ role, message }], cleared: object }}
 */
export const removeVendorProfileMediaRoles = async (remove, vendorType, vendorId, roles) => {
  const wanted = (roles || []).filter((role) => rulesFor(vendorType)[role]);
  const outcomes = await Promise.allSettled(
    wanted.map((role) => remove(vendorType, vendorId, role))
  );
  const removed = [];
  const failed = [];
  outcomes.forEach((outcome, i) => {
    const role = wanted[i];
    if (outcome.status === "fulfilled") {
      removed.push(role);
      return;
    }
    const err = outcome.reason;
    failed.push({
      role,
      message:
        (err && err.response && err.response.data && err.response.data.message) ||
        (err && err.message) ||
        "Could not remove image.",
    });
  });
  return { removed, failed, cleared: clearedVendorProfileFields(vendorType, removed) };
};
